"use client"

/*
NOTE: this file only temporarily resides in scratch-gui.
Nearly identical code appears in scratch-www, and the two should
eventually be consolidated.
*/

import React, { useState } from "react"
import classNames from "classnames"
import { FormattedMessage } from "react-intl"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import MenuBarMenu from "./menu-bar-menu.jsx"
import { MenuSection } from "../menu/menu.jsx"
import MenuItemContainer from "../../containers/menu-item.jsx"
import UserAvatar from "./user-avatar.jsx"
import MyStuff from "../my-stuff/my-stuff.jsx"
import dropdownCaret from "./dropdown-caret.svg"
import apiClient from "../../utils/apiClient"
import { cleanAllOverlays } from "../../lib/overlay-cleaner"
import ProfileModal from './ProfileModal';
import SettingsModal from './SettingsModal';
import DashboardModal from './DashboardModal';
import { setProjectTitle } from '../../reducers/project-title';

import styles from "./account-nav.css"

// Improved SVG placeholder with better styling
const defaultProfileImage =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZmZmZmZmIiBzdHJrZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0yMCAyMXYtMmE0IDQgMCAwMC00LTRIOGE0IDQgMCAwMC00IDR2MiI+PC9wYXRoPjxjaXJjbGUgY3g9IjEyIiBjeT0iNyIgcj0iNCI+PC9jaXJjbGU+PC9zdmc+"

const AccountNavComponent = ({
    className,
    classroomId,
    isEducator,
    isOpen,
    isRtl,
    isStudent,
    menuBarMenuClassName,
    onClick,
    onClose,
    onLogOut,
    onSetProjectTitle,
    profileUrl,
    thumbnailUrl,
    username,
    vm // Add vm to props
}) => {
    const [isMyStuffOpen, setIsMyStuffOpen] = useState(false)
    const [displayName, setDisplayName] = useState("")
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isDashboardOpen, setIsDashboardOpen] = useState(false);
    const [userRole, setUserRole] = useState(localStorage.getItem("userRole") || "user");

    // Updated useEffect to use apiClient
    React.useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userId = localStorage.getItem("userId")
                if (!userId) return

                const response = await apiClient.get(`/api/users/${userId}`);
                const userData = response.data
                if (userData.role) {
                    setUserRole(userData.role);
                    localStorage.setItem("userRole", userData.role);
                }
                // Truncate name if it's too long (showing first 10 characters + "...")
                const name = userData.name?.split(" ")[0] || userData.username || "User"
                setDisplayName(name.length > 10 ? `${name.substring(0, 10)}...` : name)
            } catch (error) {
                console.error("Error fetching user data:", error)
                setDisplayName("User")
                // If there's an authentication error, clear the stored data
                if (error.response?.status === 401) {
                    try {
                        const sessionId = localStorage.getItem("sessionId")
                        await apiClient.post('/auth/logout', {
                            sessionId: sessionId
                        });
                        // Clear local storage
                        localStorage.removeItem("accessToken")
                        localStorage.removeItem("userId");
                        localStorage.removeItem('sessionId');
                    } catch (err){
                        console.error('Error during logout:', error);
                        // Still redirect to login page even if the server call fails
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem("userId");
                        localStorage.removeItem('sessionId');
                    }
          if (onLogOut) onLogOut()
                }
            }
        }

        fetchUserData()
    }, [onLogOut])

    const handleMyStuffClick = () => {
        setIsMyStuffOpen(true)
        onClose()
    }

  const handleLogout = async () => {
    try {
      const sessionId = localStorage.getItem("sessionId")
      await apiClient.post('/auth/logout', {
                sessionId: sessionId
            });
      // Clear local storage
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userId");
      localStorage.removeItem('sessionId');
      localStorage.removeItem('userRole');
      localStorage.removeItem('accessibleModules');
    } catch (err){
      console.error('Error during logout:', error);
      // Still redirect to login page even if the server call fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem("userId");
      localStorage.removeItem('sessionId');
      localStorage.removeItem('userRole');
      localStorage.removeItem('accessibleModules');
    }

    if (onLogOut) onLogOut()
    // Redirect to home or login page
    window.location.href = "/"
  }

    return (
        <React.Fragment>
            <div className={classNames(styles.userInfo, className)} onClick={onClick}>
                <UserAvatar className={styles.avatar} imageUrl={thumbnailUrl || defaultProfileImage} />
                <span className={styles.profileName}>{displayName}</span>
                <div className={classNames(styles.dropdownCaretPosition, { [styles.active]: isOpen })}>
                    <img className={styles.dropdownCaretIcon} src={dropdownCaret || "/placeholder.svg"} alt="" />
                </div>
            </div>
            <MenuBarMenu
                className={menuBarMenuClassName}
                open={isOpen}
                place={isRtl ? "right" : "left"}
                onRequestClose={onClose}
            >
                <MenuItemContainer onClick={() => {
                    setIsProfileOpen(true);
                    onClose();
                }}>
                    <FormattedMessage
                        defaultMessage="Profile"
                        description="Text to link to my user profile, in the account navigation menu"
                        id="gui.accountMenu.profile"
                    />
                </MenuItemContainer>
                <MenuItemContainer onClick={handleMyStuffClick}>
                    <FormattedMessage
                        defaultMessage="My Projects"
                        description="Text to open my projects modal"
                        id="gui.accountMenu.myProjects"
                    />
                </MenuItemContainer>
                {isEducator ? (
                    <MenuItemContainer href="/educators/classes/">
                        <FormattedMessage
                            defaultMessage="My Classes"
                            description="Text to link to my classes (if I am a teacher), in the account navigation menu"
                            id="gui.accountMenu.myClasses"
                        />
                    </MenuItemContainer>
                ) : null}
                {isStudent ? (
                    <MenuItemContainer href={`/classes/${classroomId}/`}>
                        <FormattedMessage
                            defaultMessage="My Class"
                            description="Text to link to my class (if I am a student), in the account navigation menu"
                            id="gui.accountMenu.myClass"
                        />
                    </MenuItemContainer>
                ) : null}
                <MenuItemContainer onClick={() => {
                    setIsDashboardOpen(true);
                    onClose();
                }}>
                    <FormattedMessage
                        defaultMessage="Dashboard"
                        description="Text to link to user dashboard, in the account navigation menu"
                        id="gui.accountMenu.dashboard"
                    />
                </MenuItemContainer>
                {userRole === 'admin' && (
                    <MenuItemContainer onClick={() => {
                        window.location.hash = '#admin';
                        window.dispatchEvent(new CustomEvent('open-admin-dashboard', { detail: { open: true } }));
                        onClose();
                    }}>
                        <span style={{ color: '#38bdf8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🛡️</span>
                            <span>Admin Console</span>
                        </span>
                    </MenuItemContainer>
                )}
                <MenuItemContainer onClick={() => {
                    setIsSettingsOpen(true);
                    onClose();
                }}>
                    <FormattedMessage
                        defaultMessage="Account settings"
                        description="Text to link to my account settings, in the account navigation menu"
                        id="gui.accountMenu.accountSettings"
                    />
                </MenuItemContainer>
                <MenuSection>
                    <MenuItemContainer onClick={handleLogout}>
                        <FormattedMessage
                            defaultMessage="Sign out"
                            description="Text to link to sign out, in the account navigation menu"
                            id="gui.accountMenu.signOut"
                        />
                    </MenuItemContainer>
                </MenuSection>
            </MenuBarMenu>

            {isMyStuffOpen && (
                <MyStuff 
                    onClose={() => setIsMyStuffOpen(false)}
                    onNewProject={() => {
                        window.location.href = '/';
                    }}
                    onLoadProject={async (projectData, projectTitle, projectId) => {
                        cleanAllOverlays(vm);
                        if (!vm) {
                            console.error('VM instance not available');
                            return;
                        }
                        try {
                            cleanAllOverlays(vm);
                            await vm.loadProject(projectData);
                            cleanAllOverlays(vm);
                            if (projectTitle && typeof onSetProjectTitle === 'function') {
                                onSetProjectTitle(projectTitle);
                            }
                            if (projectId) {
                                localStorage.setItem('currentProjectId', projectId);
                                if (projectTitle) {
                                    localStorage.setItem('currentProjectOriginalTitle', projectTitle);
                                }
                            }
                            window.dispatchEvent(new CustomEvent('project-loaded', {
                                detail: { projectId, projectTitle }
                            }));
                            setIsMyStuffOpen(false);
                        } catch (error) {
                            console.error('Failed to load project:', error);
                            // Show error to user
                            alert('Failed to load project. Please try again.');
                        }
                    }}
                />
            )}
            {isProfileOpen && (
                <ProfileModal
                    isOpen={isProfileOpen}
                    onClose={() => setIsProfileOpen(false)}
                />
            )}
            {isSettingsOpen && (
                <SettingsModal
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                />
            )}
            {isDashboardOpen && (
                <DashboardModal
                    isOpen={isDashboardOpen}
                    onClose={() => setIsDashboardOpen(false)}
                />
            )}
        </React.Fragment>
    )
}

// Add PropTypes for better documentation and validation
AccountNavComponent.propTypes = {
    className: PropTypes.string,
    classroomId: PropTypes.string,
    isEducator: PropTypes.bool,
    isOpen: PropTypes.bool,
    isRtl: PropTypes.bool,
    isStudent: PropTypes.bool,
    menuBarMenuClassName: PropTypes.string,
    onClick: PropTypes.func,
    onClose: PropTypes.func,
    onLogOut: PropTypes.func,
    onSetProjectTitle: PropTypes.func,
    profileUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    username: PropTypes.string,
    vm: PropTypes.shape({
        loadProject: PropTypes.func.isRequired
    })
}

// Update mapStateToProps to include vm
const mapStateToProps = state => ({
    vm: state.scratchGui.vm
});

const mapDispatchToProps = (dispatch) => ({
    onLogOut: () => {
        dispatch({ type: "LOGOUT" }) // Add this action type to your Redux store
    },
    onSetProjectTitle: (title) => {
        dispatch(setProjectTitle(title));
    }
})

export default connect(mapStateToProps, mapDispatchToProps)(AccountNavComponent)
